// src/data/mockData.ts

/* ======================================================
   CORE TYPES USED ACROSS THE APP
====================================================== */

export type Client = {
  id: string;
  name: string;

  // ClientsPage uses these
  address?: string;
  phone?: string;
  email?: string;
  notes?: string;
};

export type Project = {
  id: string;
  clientId: string;
  name: string;
  address: string;

  totalEstimate: number; // derived from Scope
  status: string; // "Active" / "Settled"
  archived?: boolean;
};

export type PaymentMethod = "Cash" | "Bank";

export type ProjectTransfer = {
  id: string;
  sourceProjectId: string;
  destinationProjectId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  date: string;
  notes?: string;
};

export type PaymentReceived = {
  id: string;
  projectId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  date: string;
  notes?: string;
};

/**
 * Option B cleaner accounting:
 * - Expenses represent TRUE COSTS only (Expense + Sub-Contract Payment)
 * - Partner advances are NOT stored here; they live in PartnerDisbursements
 */
export type ExpenseType = "Expense" | "Sub-Contract Payment";

export type Expense = {
  id: string;
  projectId: string;

  expenseType: ExpenseType;

  whoPaid: string; // partner name or "Company"
  amount: number;
  paymentMethod: PaymentMethod;
  date: string;
  notes?: string;

  // Link to a SubContract when expenseType === "Sub-Contract Payment"
  subContractId?: string;
};

export type ScopeItem = {
  id: string;
  projectId: string;
  jobArea: string;
  description: string;
  estimate: number;
};

export type SubContractor = {
  id: string;
  name: string;

  // ResourcesPage UI uses these
  companyName?: string;
  phone?: string;
  email?: string;
  notes?: string;
};

export type SubContract = {
  id: string;
  projectId: string;

  // REQUIRED by ExpensesPage dropdown and SubContractsPage table
  title: string;

  projectAddress?: string;
  subContractorId: string;

   // optional additional description
  scope?: string;

  contractAmount: number;
};

export type Resource = {
  id: string;
  name: string;

  // Make dayRate REQUIRED so WagesPage is safe
  role?: string;
  dayRate: number;

  effectiveDate?: string;
  endDate?: string;
  notes?: string;
};

export type WageEntry = {
  id: string;
  year: number;
  weekNumber: number;
  resourceId: string;
  projectId: string;

  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;

  daysWorked: number;
  totalWages: number;
  adjustments?: number;
  description?: string;
  // NEW (optional) — supports Pay This Week workflow
  paid?: boolean;
  paidDate?: string; // YYYY-MM-DD
  paidMethod?: "Cash" | "Bank"; // optional
  paidNotes?: string; // optional
};

export type Partner = {
  id: string;
  name: string;
};

export type PartnerDisbursementKind = "Advance" | "Settlement" | "Other";

export type PartnerDisbursement = {
  id: string;
  projectId: string;
  partnerId: string;
  amount: number;
  method: PaymentMethod;
  date: string;
  notes?: string;
  kind?: PartnerDisbursementKind;
};

/* ======================================================
   DEFAULT PARTNERS
====================================================== */

export const defaultPartners: Partner[] = [
  { id: "P-1", name: "Manuel" },
  { id: "P-2", name: "Hitesh" },
];
